/**
 * How S7's maintenance jobs should READ — pure, so the rule is testable and
 * survives being copied.
 *
 * ⚠ THE CONTRACT lexserver ASSERTS SERVER-SIDE, RESTATED HERE BECAUSE THE UI IS
 * WHERE IT GETS LOST: a job is `null`, `ok`, or `failed` — THREE states.
 * Folding `null` into `ok` turns "no news" into "good news" on the surface an
 * operator consults precisely when something already feels wrong.
 *
 * ⚠⚠ BUT `null` DOES NOT MEAN "NEVER RAN", AND THIS FILE SAID IT DID FOR ABOUT
 * TWO HOURS. The outcomes are held in an in-process Map, so a lexserver restart
 * — any deploy, any `run-local.sh start`, any crash — wipes all three back to
 * `null`. Rendering that as "never ran" is a FALSE ALARM on three healthy jobs
 * after every routine deploy, and a panel that cries wolf on a deploy is one an
 * operator stops reading. That is the same cost this module already priced
 * correctly for `OVERDUE_CADENCES`, arriving through a different door.
 *
 * ⇒ The honest reading of today's payload is the WEAKER claim: **no outcome
 * recorded in this process**. The data cannot distinguish "never ran" from
 * "ran before the last restart", so the UI must not pick one. Same rule as the
 * roster's `unknown` and S7's `unread` — when the evidence underdetermines two
 * readings, say the thing that is true of both.
 * ⇒ The kind is `no-outcome`, not `never-ran`, because a name is a claim and
 * this one was being made in the type where nobody re-reads it.
 * ⇒ `outcomeGap()` below turns this from merely-honest into DIAGNOSTIC, using
 * the process start (`since`) lexserver added for exactly that — see its
 * docstring.
 *
 * ⚠ AND `scheduled` IS NOT A HEALTH SIGNAL. It is a fact about timers, and
 * timers are created just as happily by a job that throws on every tick as by a
 * healthy one — which is exactly why this endpoint had to exist before S7 could
 * show a jobs panel at all. Before it, the panel would have been structurally
 * incapable of ever reporting a fault. Never let `scheduled:true` colour a job
 * row.
 *
 * ⚠ AND A TIMESTAMP ALONE CANNOT SAY WHETHER A JOB IS HEALTHY. "ran ok" plus
 * "9 hours ago" is a STUCK 60-second job wearing a green badge. That is why the
 * endpoint ships `intervals` — the client is meant to judge age against cadence,
 * which `jobStaleness` does.
 */

import type { SpecialistJobTick } from "./specialists-api";

export type JobView =
  /** No outcome recorded IN THIS PROCESS. Not ok, not failed, not "fine so
   *  far" — and deliberately NOT "never ran", which the payload cannot support
   *  while the record is in-process. */
  | { kind: "no-outcome" }
  | { kind: "ok"; lastRunAt: string; durationMs?: number }
  | { kind: "failed"; lastRunAt: string; error: string }
  /** An outcome this client has no rendering for. Never assumed healthy. */
  | { kind: "unrecognized"; outcome: string; lastRunAt: string };

export function selectJobView(tick: SpecialistJobTick | null | undefined): JobView {
  if (!tick) return { kind: "no-outcome" };
  switch (tick.outcome) {
    case "ok":
      return { kind: "ok", lastRunAt: tick.lastRunAt, durationMs: tick.durationMs };
    case "failed":
      return {
        kind: "failed",
        lastRunAt: tick.lastRunAt,
        // Never fabricate an error; never render a blank one either.
        error: tick.error?.trim() || "reported failed, without a stated error",
      };
    default:
      return { kind: "unrecognized", outcome: String(tick.outcome), lastRunAt: tick.lastRunAt };
  }
}

/**
 * How many cadences may elapse before a job counts as overdue.
 *
 * 3 rather than 1 because a tick is scheduled, not guaranteed instantaneous:
 * a single missed beat on a 60s timer is jitter, not an incident. Three
 * consecutive misses is a pattern. Deliberately not 1 — an alarm that fires on
 * normal operation is one an operator learns to ignore, which costs more than
 * the alarm was worth.
 */
export const OVERDUE_CADENCES = 3;

export interface Staleness {
  ageMs: number;
  /** True when the last run is older than `OVERDUE_CADENCES` intervals. */
  overdue: boolean;
}

/**
 * Judge a job's age against its own cadence.
 *
 * Returns `null` when the judgment cannot be made — no run yet, or no interval
 * to judge against. ⚠ `null` means "cannot say", NOT "fine": a caller that
 * renders a missing staleness as healthy has reintroduced the very collapse
 * this module exists to prevent.
 */
export function jobStaleness(
  view: JobView,
  intervalMs: number | undefined,
  now: number,
): Staleness | null {
  if (view.kind === "no-outcome") return null;
  if (!intervalMs || intervalMs <= 0) return null;
  const last = Date.parse(view.lastRunAt);
  if (!Number.isFinite(last)) return null;
  const ageMs = now - last;
  return { ageMs, overdue: ageMs > intervalMs * OVERDUE_CADENCES };
}

/** Human age, coarse on purpose — an operator wants the order of magnitude. */
export function describeAge(ageMs: number): string {
  const s = Math.max(0, Math.round(ageMs / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

/**
 * Whether a MISSING outcome is expected or alarming — the judgment that makes
 * `no-outcome` diagnostic instead of merely honest.
 *
 * Takes the process start — `since` on the `/jobs` payload, added for exactly
 * this. The reasoning is the same cadence arithmetic `jobStaleness` uses,
 * pointed at absence instead of age:
 *
 *   - restarted less than one cadence ago → the job has not had its turn yet.
 *     EXPECTED. Say so quietly; this is the routine-deploy case that was
 *     firing a false alarm.
 *   - restarted more than `OVERDUE_CADENCES` cadences ago and STILL nothing →
 *     the job should have run in this process and did not. That is a real
 *     fault signal, and it is one durable persistence would NOT provide:
 *     a durable "last ran yesterday" says nothing about whether the CURRENT
 *     process is firing at all.
 *   - in between → cannot say. Neither reassure nor alarm.
 *
 * Returns `null` when the judgment cannot be made at all (no start time, no
 * interval). ⚠ As everywhere in this module, `null` is "cannot say", never
 * "fine".
 */
export type OutcomeGap = "expected" | "overdue" | "indeterminate";

export function outcomeGap(
  view: JobView,
  intervalMs: number | undefined,
  /** The process start (`since`). Undefined on an older lexserver. */
  processStart: string | undefined,
  now: number,
): OutcomeGap | null {
  if (view.kind !== "no-outcome") return null;
  if (!intervalMs || intervalMs <= 0 || !processStart) return null;
  const started = Date.parse(processStart);
  if (!Number.isFinite(started)) return null;
  const upMs = now - started;
  if (upMs < intervalMs) return "expected";
  if (upMs > intervalMs * OVERDUE_CADENCES) return "overdue";
  return "indeterminate";
}
